import { Button, Dialog, DialogActions, DialogContent, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PermissionAssignmentWithEntityId } from "./Domain/department-monitoring.types";
import { PermissionAssignmentInfoDto } from "../../../hooks/Remote/Capacity/DepartmentMonitoring/useRemotePostMonitoringQuota";
import { useEffect, useState } from "react";

type Props = {
  data: PermissionAssignmentWithEntityId;
  isOpen: boolean;
  setEditOpen: (o: boolean) => void;
  handleEditQuota: (dto: Omit<PermissionAssignmentInfoDto, "costCenterUnitIds">) => void;
};
export function MonitoringQuotaEditDialog({
  data,
  isOpen,
  setEditOpen,
  handleEditQuota
}: Readonly<Props>) {
  const { t } = useTranslation();

  const [inputError, setInputError] = useState(false);
  const [quotaVal, setQuotaVal] = useState<Omit<PermissionAssignmentInfoDto, "costCenterUnitIds">>({
    targetEmployeeQuota: data.targetEmployeeQuota,
    targetSupervisorQuota: data.targetSupervisorQuota
  });

  const handleCancelEdit = () => {
    setQuotaVal({
      targetEmployeeQuota: data.targetEmployeeQuota,
      targetSupervisorQuota: data.targetSupervisorQuota
    });
    setInputError(false);
    setEditOpen(false);
  };

  const handleConfirmEdit = () => {
    if (quotaVal.targetEmployeeQuota < 0 || quotaVal.targetSupervisorQuota < 0)
      return setInputError(true);

    handleEditQuota({
      targetEmployeeQuota: quotaVal.targetEmployeeQuota,
      targetSupervisorQuota: quotaVal.targetSupervisorQuota
    });
    setEditOpen(false);
  };

  useEffect(() => {
    setQuotaVal({
      targetEmployeeQuota: data.targetEmployeeQuota,
      targetSupervisorQuota: data.targetSupervisorQuota
    });
  }, [data]);

  return (
    <Dialog
      data-testid={"monitoring-quota-edit-dialog"}
      onClose={handleCancelEdit}
      aria-labelledby="simple-dialog-title"
      open={isOpen}
    >
      <DialogContent
        sx={{ display: "flex", justifyContent: "flex-end", flexDirection: "column", minWidth: 250 }}
      >
        <Typography sx={{ mb: 2 }}>{t("Edit Target Quota")}</Typography>

        {/* edit employee quota field */}
        <TextField
          label={`${t("targetEmployeeQuota")} (%)`}
          sx={{ py: 1 }}
          type="number"
          inputProps={{ min: 0 }}
          value={quotaVal.targetEmployeeQuota}
          data-testid="edit-employee-quota-field"
          onChange={e => {
            setInputError(false);
            setQuotaVal(prev => ({ ...prev, targetEmployeeQuota: Number(e.target.value) }));
          }}
        />

        {/* edit supervisor quota field */}
        <TextField
          label={`${t("targetSupervisorQuota")} (%)`}
          sx={{ py: 1 }}
          type="number"
          inputProps={{ min: 0 }}
          value={quotaVal.targetSupervisorQuota}
          data-testid="edit-supervisor-quota-field"
          onChange={e => {
            setInputError(false);
            setQuotaVal(prev => ({ ...prev, targetSupervisorQuota: Number(e.target.value) }));
          }}
        />

        {/* show input error */}
        {inputError && (
          <Typography variant={"body2"} color={"red"}>
            {t("quota must be bigger than 0")}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          color={"secondary"}
          onClick={handleCancelEdit}
          data-testid="edit-quota-dialog-btn-Cancel"
        >
          {t("Cancel")}
        </Button>

        <Button onClick={handleConfirmEdit} data-testid="edit-quota-dialog-btn-Confirm">
          {t("Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
